import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { formatPhoneDisplay } from '@/util/phoneFormatter'
import { pageOffset, readPagingQuery, syncPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import SmsConversationDrawer from './SMSConversationDrawer.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SMSLog',
  emits: ["switch"],
  setup(__props, { emit }: { emit: ({ (e: 'switch', allMessages: boolean): void }), expose: any, slots: any, attrs: any }) {

// SMS log tab: every sms_events row of the current group, newest first, with the
// delivery status the status webhook writes. Clicking a row opens the customer's
// conversation (the chat window). Filters, sort and paging are server side
// (get-sms-log-for-group).

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/sms-log`

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued', sending: 'Sending', sent: 'Sent', delivered: 'Delivered',
  undelivered: 'Undelivered', failed: 'Failed', received: 'Received'
}
// Same palette the app uses for success, info, danger and primary
const STATUS_COLORS: Record<string, string> = {
  delivered: '#00f2c3', sent: '#1d8cf8', sending: '#1d8cf8', queued: '#1d8cf8',
  undelivered: '#fd5d93', failed: '#fd5d93', received: '#e14eca'
}

const loading = ref(true)
const events = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(25)
const perPageOptions = [10, 25, 50, 100]
const sort = ref<{ key: string; dir: 'asc' | 'desc' }>({ key: 'timestamp', dir: 'desc' })
const sortOptions = [
  { key: 'timestamp', label: 'Sent' },
  { key: 'status', label: 'Status' }
]
const filterValues = ref<Record<string, any>>({ phone: '', status: [], direction: '', sent_date: null, every_message: true })
const isMobile = ref(false)
const drawerOpen = ref(false)
// The thread opens by conversation id when the row has one, else by the customer's number
const selectedConversationId = ref<number | null>(null)
const selectedPhone = ref<string | null>(null)
let didInitialFetch = false

const filterSchema = computed(() => [
  { key: 'phone', type: 'text', label: 'Phone number', placeholder: 'Customer phone', operatorLabel: 'contains', primary: true },
  { key: 'status', type: 'multiselect', label: 'Status', primary: true,
    options: Object.keys(STATUS_LABELS).map((value) => ({ label: STATUS_LABELS[value], value })) },
  { key: 'direction', type: 'select', label: 'Direction', primary: true,
    options: [{ label: 'Sent', value: 'outbound' }, { label: 'Received', value: 'inbound' }] },
  { key: 'sent_date', type: 'daterange', label: 'Sent date', primary: true },
  { key: 'every_message', type: 'toggle', label: 'Every message', primary: true }
])

const columns = [
  { key: 'timestamp', label: 'Sent', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'to_e164', label: 'To' },
  { key: 'source', label: 'Source' },
  { key: 'num_segments', label: 'Segments', align: 'right' as const }
]

const hasActiveFilters = computed(() => {
  const f = filterValues.value
  return !!(f.phone || (f.status && f.status.length) || f.direction || (f.sent_date && (f.sent_date.from || f.sent_date.to)))
})

const canDownload = computed(() => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(permissions) && permissions.includes('download_data')
  } catch (e) {
    return false
  }
})

function statusLabel(status: string) {
  return STATUS_LABELS[status] || status
}
function statusColor(status: string) {
  return STATUS_COLORS[status] || '#7d7d7d'
}
function formatWhen(value: string) {
  if (!value) return ''
  try {
    return formatGroupDate(value) + ' ' + formatGroupTime(value)
  } catch (e) {
    return String(value).slice(0, 16)
  }
}
function recipientName(row: any) {
  return [row.recipient_first_name, row.recipient_last_name].filter(Boolean).join(' ')
}
function sourceLabel(row: any) {
  if (row.direction === 'inbound') return 'Customer reply'
  if (row.template_name) return row.template_name
  if (row.automation_name) return row.automation_name
  if (row.sms_type === 'test') return 'Test message'
  return 'Manual'
}

function queryString(count: number, offset: number) {
  const params = new URLSearchParams()
  params.append('count', String(count))
  params.append('offset', String(offset))
  params.append('sort', sort.value.key)
  params.append('dir', sort.value.dir)
  const f = filterValues.value
  if (f.phone) params.append('phone', f.phone)
  if (Array.isArray(f.status) && f.status.length) params.append('status', f.status.join(','))
  if (f.direction) params.append('direction', f.direction)
  if (f.sent_date && f.sent_date.from) params.append('date_from', f.sent_date.from)
  if (f.sent_date && f.sent_date.to) params.append('date_to', f.sent_date.to)
  return params.toString()
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function fetchPage(count: number, offset: number) {
  const config = await authConfig()
  const response = await axios.get(`${baseUrl}?${queryString(count, offset)}`, config)
  return { rows: response.data.events || [], total: response.data.total_rows || 0 }
}

async function loadEvents() {
  loading.value = true
  try {
    const page = await fetchPage(perPage.value, pageOffset(currentPage.value, perPage.value))
    events.value = page.rows
    totalRows.value = page.total
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
    didInitialFetch = true
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the SMS log. Please refresh the page.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// The first change after mount is FilterBar's restore (last-used or default view). The
// last-used save is debounced, so a restore can still carry the other view's toggle state
// for a moment; it never switches views. Only an explicitly opened saved view (?view= on
// mount) or a later user change does.
const presetOnMount = !!proxy.$route.query.view
let restored = false
const filterBarEl = ref<any>(null)
function settleView(values: Record<string, any>, here: boolean): boolean {
  const first = !restored
  restored = true
  if ((values.every_message === true) === here) return true
  if (first && !presetOnMount) {
    filterValues.value.every_message = here
    if (filterBarEl.value) filterBarEl.value.applyValues({ ...values, every_message: here })
    return true
  }
  emit('switch', !here)
  return false
}
function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!settleView(filterValues.value, true)) return
  currentPage.value = 1
  loadEvents()
}
// Per page select, or a restored saved view (meta.restoring: the change that follows fetches)
function onPerPageChange(n: number, meta?: { restoring?: boolean }) {
  perPage.value = n
  currentPage.value = 1
  if (meta && meta.restoring) return
  loadEvents()
}
function onSortChange(value: { key: string; dir: 'asc' | 'desc' }, meta?: { restoring?: boolean }) {
  sort.value = { key: value.key, dir: value.dir === 'asc' ? 'asc' : 'desc' }
  if (meta && meta.restoring) return
  currentPage.value = 1
  if (didInitialFetch) loadEvents()
}
function onPageChange(page: number) {
  currentPage.value = page
  loadEvents()
}
function openEvent(row: any) {
  selectedConversationId.value = row.conversation_id || null
  selectedPhone.value = row.direction === 'inbound' ? row.from_e164 : row.to_e164
  drawerOpen.value = true
}

async function downloadCsvExport() {
  loading.value = true
  try {
    // 1,000 rows per request (the lambda's cap) so a season of mail is a handful of calls
    const rows = await fetchAllPages(fetchPage, 1000)
    // downloadCsv quotes every cell, so commas in formatted dates and message bodies stay in their column
    downloadCsv('sms_log.csv', [
      { label: 'Sent', value: (row: any) => formatGroupDate(row.timestamp, { dayOfWeek: false }) + ' ' + formatGroupTime(row.timestamp) },
      { label: 'Status', value: (row: any) => statusLabel(row.status) },
      { label: 'Direction', value: (row: any) => (row.direction === 'inbound' ? 'Received' : 'Sent') },
      { label: 'To', value: (row: any) => row.to_e164 || '' },
      { label: 'From', value: (row: any) => row.from_e164 || '' },
      { label: 'Recipient', value: (row: any) => recipientName(row) },
      { label: 'Source', value: (row: any) => sourceLabel(row) },
      { label: 'Segments', value: (row: any) => (row.num_segments == null ? '' : row.num_segments) },
      { label: 'Error code', value: (row: any) => row.provider_error_code || '' },
      { label: 'Error message', value: (row: any) => row.provider_error_message || '' },
      { label: 'Message', value: (row: any) => row.body || '' }
    ], rows)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the SMS log. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// DataTable hides its headers at 767px, so phones sort from the filter bar chip
const mobileMql = window.matchMedia('(max-width: 767px)')
const onMobileMql = () => { isMobile.value = mobileMql.matches }

onMounted(() => {
  onMobileMql()
  mobileMql.addEventListener('change', onMobileMql)
  const paging = readPagingQuery(proxy.$route, 25)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 25
  currentPage.value = paging.page
  loadEvents()
})
onBeforeUnmount(() => {
  mobileMql.removeEventListener('change', onMobileMql)
})

return { __sfc: true,emit, proxy, companyId, companyGroupId, baseUrl, STATUS_LABELS, STATUS_COLORS, loading, events, totalRows, currentPage, perPage, perPageOptions, sort, sortOptions, filterValues, isMobile, drawerOpen, selectedConversationId, selectedPhone, didInitialFetch, filterSchema, columns, hasActiveFilters, canDownload, statusLabel, statusColor, formatWhen, recipientName, sourceLabel, queryString, authConfig, fetchPage, loadEvents, presetOnMount, restored, filterBarEl, settleView, onFiltersChanged, onPerPageChange, onSortChange, onPageChange, openEvent, downloadCsvExport, mobileMql, onMobileMql, tagChipStyle, formatPhoneDisplay, SmsConversationDrawer }
}

})